import * as React from 'react'

import * as ToastPrimitives from '@radix-ui/react-toast'
import { cva, type VariantProps } from 'class-variance-authority'
import { XIcon } from 'lucide-react'

import { cn } from '@/shared/utils/helpers'

const ToastProvider = ToastPrimitives.Provider

const ToastViewport = React.forwardRef<
	React.ComponentRef<typeof ToastPrimitives.Viewport>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Viewport>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Viewport
		ref={ref}
		className={cn(
			'fixed top-0 z-[100] flex max-h-screen w-full flex-col-reverse p-4 sm:right-0 sm:top-0 sm:flex-col md:max-w-[420px]',
			className
		)}
		{...props}
	/>
))
ToastViewport.displayName = ToastPrimitives.Viewport.displayName

const toastVariants = cva(
	'group pointer-events-auto relative flex w-full items-center justify-between space-x-2 overflow-hidden rounded-md border p-4 pr-6 shadow-lg transition-all data-[swipe=cancel]:translate-x-0 data-[swipe=end]:translate-x-[var(--radix-toast-swipe-end-x)] data-[swipe=move]:translate-x-[var(--radix-toast-swipe-move-x)] data-[swipe=move]:transition-none data-[state=open]:animate-in data-[state=closed]:animate-out data-[swipe=end]:animate-out data-[state=closed]:fade-out-80 data-[state=closed]:slide-out-to-right-full data-[state=open]:slide-in-from-top-full data-[state=open]:sm:slide-in-from-bottom-full',
	{
		variants: {
			variant: {
				default: 'border bg-background text-foreground',
				success:
					'success group border-[color:var(--color-feedback-success)] bg-[color:var(--color-feedback-success)]/80 text-foreground dark:border-[color:var(--color-feedback-success)] dark:bg-[color:var(--color-feedback-success)]/80 dark:text-foreground',
				error:
					'destructive group border-[color:var(--color-feedback-error)] bg-[color:var(--color-feedback-error)]/80 text-foreground dark:border-[color:var(--color-feedback-error)] dark:bg-[color:var(--color-feedback-error)]/80 dark:text-foreground',
				warning:
					'warning group border-[color:var(--color-feedback-warning)] bg-[color:var(--color-feedback-warning)]/80 text-foreground dark:border-[color:var(--color-feedback-warning)] dark:bg-[color:var(--color-feedback-warning)]/80 dark:text-foreground',
				info: 'info group border-[color:var(--color-feedback-info)] bg-[color:var(--color-feedback-info)]/80 text-foreground dark:border-[color:var(--color-feedback-info)] dark:bg-[color:var(--color-feedback-info)]/80 dark:text-foreground',
			},
		},
		defaultVariants: {
			variant: 'default',
		},
	}
)

const Toast = React.forwardRef<
	React.ComponentRef<typeof ToastPrimitives.Root>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Root> &
		VariantProps<typeof toastVariants>
>(({ className, variant, ...props }, ref) => {
	return (
		<ToastPrimitives.Root
			ref={ref}
			className={cn(toastVariants({ variant }), className)}
			{...props}
		/>
	)
})
Toast.displayName = ToastPrimitives.Root.displayName

const ToastAction = React.forwardRef<
	React.ComponentRef<typeof ToastPrimitives.Action>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Action>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Action
		ref={ref}
		className={cn(
			'inline-flex h-8 shrink-0 items-center justify-center rounded-md border bg-transparent px-3 text-sm font-medium transition-colors hover:bg-secondary focus:outline-none focus:ring-1 focus:ring-ring disabled:pointer-events-none disabled:opacity-50 group-[.success]:border-[color:var(--color-feedback-success)]/40 group-[.success]:hover:border-[color:var(--color-feedback-success)]/60 group-[.success]:hover:bg-[color:var(--color-feedback-success)]/20 group-[.success]:focus:ring-[color:var(--color-feedback-success)] group-[.destructive]:border-[color:var(--color-feedback-error)]/40 group-[.destructive]:hover:border-[color:var(--color-feedback-error)]/60 group-[.destructive]:hover:bg-[color:var(--color-feedback-error)]/20 group-[.destructive]:focus:ring-[color:var(--color-feedback-error)] group-[.warning]:border-[color:var(--color-feedback-warning)]/40 group-[.warning]:hover:border-[color:var(--color-feedback-warning)]/60 group-[.warning]:hover:bg-[color:var(--color-feedback-warning)]/20 group-[.warning]:focus:ring-[color:var(--color-feedback-warning)] group-[.info]:border-[color:var(--color-feedback-info)]/40 group-[.info]:hover:border-[color:var(--color-feedback-info)]/60 group-[.info]:hover:bg-[color:var(--color-feedback-info)]/20 group-[.info]:focus:ring-[color:var(--color-feedback-info)]',
			className
		)}
		{...props}
	/>
))
ToastAction.displayName = ToastPrimitives.Action.displayName

const ToastClose = React.forwardRef<
	React.ComponentRef<typeof ToastPrimitives.Close>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Close>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Close
		ref={ref}
		className={cn(
			'absolute right-1 top-1 rounded-md p-1 text-foreground/50 opacity-0 transition-opacity hover:text-foreground focus:opacity-100 focus:outline-none focus:ring-1 group-hover:opacity-100 group-[.success]:text-[color:var(--color-feedback-success)] group-[.success]:hover:text-[color:var(--color-feedback-success)] group-[.destructive]:text-[color:var(--color-feedback-error)] group-[.destructive]:hover:text-[color:var(--color-feedback-error)] group-[.warning]:text-[color:var(--color-feedback-warning)] group-[.warning]:hover:text-[color:var(--color-feedback-warning)] group-[.info]:text-[color:var(--color-feedback-info)] group-[.info]:hover:text-[color:var(--color-feedback-info)]',
			className
		)}
		toast-close=""
		{...props}
	>
		<XIcon className="h-4 w-4" />
	</ToastPrimitives.Close>
))
ToastClose.displayName = ToastPrimitives.Close.displayName

const ToastTitle = React.forwardRef<
	React.ComponentRef<typeof ToastPrimitives.Title>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Title>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Title
		ref={ref}
		className={cn('text-sm font-semibold [&+div]:text-xs', className)}
		{...props}
	/>
))
ToastTitle.displayName = ToastPrimitives.Title.displayName

const ToastDescription = React.forwardRef<
	React.ComponentRef<typeof ToastPrimitives.Description>,
	React.ComponentPropsWithoutRef<typeof ToastPrimitives.Description>
>(({ className, ...props }, ref) => (
	<ToastPrimitives.Description
		ref={ref}
		className={cn('text-sm opacity-90', className)}
		{...props}
	/>
))
ToastDescription.displayName = ToastPrimitives.Description.displayName

type ToastProps = React.ComponentPropsWithoutRef<typeof Toast>

type ToastActionElement = React.ReactElement<typeof ToastAction>

export {
	type ToastProps,
	type ToastActionElement,
	ToastProvider,
	ToastViewport,
	Toast,
	ToastTitle,
	ToastDescription,
	ToastClose,
	ToastAction,
}
